import { useEffect, useState } from "react";
import background from "../images/background.svg";
import { CardElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { stripeService } from "../services/stripe.service";
import { userService } from "../services/user.service";
import Alert from "../components/Alert";
import { doLogout, doRefreshUser, useAuthDispatch, useAuthState } from "../context/AuthContext";
import SuccessAlert from "../components/SuccessAlert";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { purchaseLinkService } from "../services/link.service";
import userDashboard from "./UserDashboard";
import { dashboardService } from "../services/dashboard.service";

const Subscribe = (props) => {
  const [success, setSuccess] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [inputs, setInputs] = useState({});
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState({});
  const [paymentLinkError, setPaymentLinkError] = useState("");

  const { user } = useAuthState();
  const dispatch = useAuthDispatch();
  const navigate = useNavigate();
  const { key } = useParams();
  const elements = useElements();
  const stripe = useStripe();

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    if (!user.licence) {
      setCheckoutError("You don't have a licence");
    }

    if (user.licence.current_subscription_id) {
      setCheckoutError("You already have an active licence");
    }
    setLoading(false);
  };

  const handleChange = (e) =>
    setInputs((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));

  const handlePaymentIntent = async (paymentIntent) => {
    console.log(paymentIntent);
    if (paymentIntent.status === "succeeded" || paymentIntent.status === "paid") {
      setSuccess("Successfully Subscribed. Please check your email inbox for instructions! You will redirected momentarily.");

      setTimeout(() => {
        navigate("/dashboard");
      }, 10000);

    } else if (paymentIntent.status === "requires_action") {
      //The user needs to authenticate with 3DS
      const confirmationResult = await stripe.confirmCardPayment(paymentIntent.client_secret);
      if (confirmationResult.error) {
        setErrorAndReset(confirmationResult.error.message);
        return;
      }

      await handlePaymentIntent(confirmationResult.paymentIntent);
    } else if (paymentIntent.status === "requires_payment_method") {
      //Declined
      setErrorAndReset("Payment Declined");
    } else if (paymentIntent.status === "canceled") {
      //Cancelled
      setErrorAndReset("Payment Cancelled");
    } else if (paymentIntent.status === "requires_confirmation") {
      const confirmationResult = await stripe.confirmCardPayment(paymentIntent.client_secret);
      if (confirmationResult.error) {
        setCheckoutError(confirmationResult.error.message);
        return;
      }
      await handlePaymentIntent(confirmationResult.paymentIntent);
    } else {
      setErrorAndReset("Error during payment");
    }
  };

  const setErrorAndReset = (message) => {
    setCheckoutError(message);
    setProcessing(false);
  };

  const handleLogout = async () => {
    await doLogout(dispatch);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setCheckoutError("");

    const cardElement = elements.getElement(CardElement);
    const billingDetails = {
      email: inputs.email,
      name: inputs.full_name,
      address: {
        line1: inputs.billing_address1,
        line2: inputs.billing_address2,
        city: inputs.billing_city,
        postal_code: inputs.billing_postcode,
        country: "GB"
      }
    };

    const { paymentMethod, error: paymentError } = await stripe.createPaymentMethod({
      type: "card",
      billing_details: billingDetails,
      card: cardElement
    });

    if (paymentError) {
      setErrorAndReset(paymentError.message);
      return;
    }

    const { data, error } = await stripeService.resubscribe(paymentMethod.id, billingDetails, user.id);
    if (error) {
      setErrorAndReset(error.details);
      return;
    }

    if (data.payment_intent.status === 'paid'){
      setSuccess("Successfully Subscribed. Please check your email inbox for instructions! You will redirected momentarily.");

      setTimeout(async () => {
        await doRefreshUser(dispatch);
        navigate("/dashboard");
      }, 10000);
    } else {
      await handlePaymentIntent(data.payment_intent.payment_intent);
    }
    setProcessing(false);
  };

  if (loading) return;

  return (
    <form>
      <div className="relative h-screen overflow-y-auto bg-indigo-900">
        <img src={background} className="absolute h-full w-full object-cover" alt="background" />
        <div className="absolute inset-0 bg-black opacity-25"></div>
        <div
          className="container relative z-10 mx-auto flex-grow items-center md:px-6 lg:max-w-6xl xl:px-3 xl:px-0 xl:py-32 ">
          <div className="md:p-5">
            <div
              className="relative mx-auto max-w-6xl md:rounded-lg overflow-hidden sm:border shadow-xl dark:border-gray-500 ">
              <div className="md:grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x ">
                {/* Billing Info */}
                <div className="bg-white dark:bg-gray-800 p-8 px-16">
                  <div className="h-full flex flex-col space-y-4 text-white">
                    <div className="space-y-4">
                      <div>
                        <div className="justify-between">
                          <div className="flex items-center justify-between">
                            <label className="inline-block mb-1 text-black dark:text-white text-sm !text-opacity-70">Email
                              Address</label>
                            {user === null ?
                              <div className="text-black dark:text-white text-sm">
                                <a className="transition hover:underline text-blue-500 dark:text-blue-400"
                                   target="_self" href="/src/pages/Login?redirect=/purchase">Already Registered?</a>
                              </div>
                              :
                              <div className="text-black dark:text-white text-sm">
                                <a onClick={handleLogout}
                                   className="transition hover:underline text-blue-500 dark:text-blue-400"
                                   target="_self" href="#">Not You?</a>
                              </div>
                            }
                          </div>
                          <input
                            onChange={handleChange}
                            name="email"
                            placeholder={user !== null ? `${user.email}` : "Email Address"}
                            disabled={user}
                            className="relative transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm rounded-md input-group:rounded-none input-group:first:rounded-l-md input-group:last:rounded-r-md input-group:-ml-px input-group:first:!ml-0 !border-gray-200 dark:!border-gray-700 focus:!border-blue-300 dark:focus:!border-blue-400 !ring-blue-400 !ring-opacity-40 " />
                        </div>
                      </div>
                      {!user &&
                        <div>
                          <div>
                            <div className="justify-between">
                              <div className="flex items-center justify-between">
                                <label
                                  className="inline-block mb-1 text-black dark:text-white text-sm !text-opacity-70">Create
                                  Account</label>
                              </div>
                              <div className="flex">
                                <input
                                  onChange={handleChange}
                                  name="password"
                                  type="password"
                                  placeholder="Password"
                                  className="relative mr-2 transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm rounded-md input-group:rounded-none input-group:first:rounded-l-md input-group:last:rounded-r-md input-group:-ml-px input-group:first:!ml-0 !border-gray-200 dark:!border-gray-700 focus:!border-blue-300 dark:focus:!border-blue-400 !ring-blue-400 !ring-opacity-40 " />
                                <input
                                  onChange={handleChange}
                                  name="confirm_password"
                                  type="password"
                                  placeholder="Confirm Password"
                                  className="relative transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm rounded-md input-group:rounded-none input-group:first:rounded-l-md input-group:last:rounded-r-md input-group:-ml-px input-group:first:!ml-0 !border-gray-200 dark:!border-gray-700 focus:!border-blue-300 dark:focus:!border-blue-400 !ring-blue-400 !ring-opacity-40 " />
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="justify-between">
                              <label className="inline-block mb-1 text-black dark:text-white text-sm !text-opacity-70">Your
                                Name</label>
                              <div className="flex">
                                <input
                                  onChange={handleChange}
                                  name="full_name"
                                  placeholder="Full Name"
                                  className="relative mr-2 transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm rounded-md input-group:rounded-none input-group:first:rounded-l-md input-group:last:rounded-r-md input-group:-ml-px input-group:first:!ml-0 !border-gray-200 dark:!border-gray-700 focus:!border-blue-300 dark:focus:!border-blue-400 !ring-blue-400 !ring-opacity-40 " />
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                    </div>


                    <div>
                      <div className="justify-between">
                        <label className="inline-block mb-1 text-black dark:text-white text-sm !text-opacity-70">Card
                          Details</label>
                        <div
                          className="h-9 text-sm transition border text-lg rounded-md text-white bg-white dark:bg-gray-900 py-2 pl-3 pr-1  ">
                          <CardElement
                            id="card-element"
                            options={{
                              hidePostalCode: true,
                              iconStyle: "solid",
                              style: {
                                base: {
                                  fontFamily: "Poppins",
                                  iconColor: "#c4f0ff",
                                  color: "#bbbbbb",
                                  fontSize: "1rem"
                                },
                                invalid: {
                                  iconColor: "#FFC7EE",
                                  color: "#FFC7EE"
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="">
                      <label className="inline-block mb-1 text-black dark:text-white text-sm !text-opacity-70">Billing
                        Address</label>
                      <input onChange={handleChange} name="billing_address1" placeholder="Address Line 1"
                             className="mb-3 rounded-none -mt-px relative transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm input-group:-ml-px input-group:first:!ml-0 !border-red-500 !ring-red-400 !ring-opacity-40 z-10 " />
                      <input onChange={handleChange} name="billing_address2" placeholder="Address Line 2"
                             className="mb-3 rounded-none -mt-px relative transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm input-group:-ml-px input-group:first:!ml-0 !border-red-500 !ring-red-400 !ring-opacity-40 z-10 " />
                      <div className="flex">
                        <input onChange={handleChange} name="billing_city" placeholder="City"
                               className="mr-2 rounded-none -mt-px relative transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm input-group:-ml-px input-group:first:!ml-0 !border-red-500 !ring-red-400 !ring-opacity-40 z-10 " />
                        <input onChange={handleChange} name="billing_postcode" placeholder="Postcode"
                               className="mb-3 rounded-none -mt-px relative transition focus:!ring focus-within:z-20 bg-white dark:bg-gray-900 placeholder-gray-300 dark:placeholder-gray-500 text-gray-800 dark:text-white py-0 px-3 w-full disabled:cursor-not-allowed h-9 text-sm input-group:-ml-px input-group:first:!ml-0 !border-red-500 !ring-red-400 !ring-opacity-40 z-10 " />
                      </div>
                    </div>
                    <span className="inline-block mb-1 text-black dark:text-white text-sm !text-opacity-70">
                    </span>
                    {checkoutError && <Alert message={checkoutError} />}
                    {success && <SuccessAlert message={success} />}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-100 dark:border-gray-500 dark:bg-gray-900 ">
                  <div className="h-full flex flex-col p-8">
                    <div className="text-lg text-white">
                      <span className="text-lg">yoghurtbot.io Subscription</span>
                    </div>
                    {/* Product Summary Text */}
                    <div className="py-3 overflow-y-auto">
                      <span className="dark:text-white text-sm !text-opacity-70">
                        You are about to start your yoghurtbot.io subscrition. Please review the details below and click Subscribe to continue.
                      </span>
                    </div>
                    {/* Order Summary Stuff */}
                    <div className="border py-3 rounded-lg shadow-xl -mx-2 p-3 dark:bg-gray-800 dark:border-gray-500">
                      <span className="text-lg text-white">Order:</span>
                      <div className="flex justify-between items-center text-white mt-2 pb-2">
                        <span>Monthly Recurring Fee</span>
                        <span>£30.00</span>
                      </div>
                      <div className="flex w-full items-center">
                        <div className="grow border-t dark:border-gray-500"></div>
                      </div>
                      <div className="flex justify-between items-center text-white mt-2 pb-2">
                        <span>Total Due Today</span>
                        <span>{user.licence.type === "prepaid" ? "£0" : "£30.00"}</span>
                      </div>
                    </div>
                    {/* Footer */}
                    <span className="dark:text-white text-sm !text-opacity-70 py-3">
                      By clicking subscribe below, you agree to the yoghurtbot.io Terms of Service and Privacy Policy.
                    </span>
                    {/* Subscribe button */}
                    <div className="flex flex-col items-center mx-5 mt-5">
                      <div className="my-4 mx-auto flex w-full justify-center">
                        <button
                          onClick={handleSubmit}
                          type="button"
                          disabled={processing || success || checkoutError}
                          className="disabled:bg-gray-700 disabled:cursor-not-allowed ico flex w-full min-w-max items-center rounded-lg bg-emerald-700 px-10 py-2 text-center text-center text-white hover:cursor-pointer hover:bg-emerald-600"

                        >
                          {processing && (
                            <svg
                              className="mr-3 h-5 w-5 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}
                          <span className="mx-auto text-center font-normal">
                            {processing ? "Processing Payment..." : success ? "Successfully Subscribed" : "Subscribe"}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default Subscribe;
